import React, { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { WarningPing } from 'shared/ui/WarningPing';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { Form } from 'antd';
import { BoxDeactivationReason, BoxForListing, useActivateBoxMutation, useDeactivateBoxMutation } from 'entities/Box';

interface ActivationBoxModalProps {
  box: BoxForListing;
  action: ReactElement;
}

export const ActivationBoxModal: FC<ActivationBoxModalProps> = memo((props) => {
  const { box, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);

  const [form] = Form.useForm();

  const { t } = useAppTranslation(['boxes', 'common']);

  const [activateBox] = useActivateBoxMutation();
  const [deactivateBox] = useDeactivateBoxMutation();

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const deactivationReasonOptions = Object.values(BoxDeactivationReason).map((option) => ({
    label: t(option),
    value: option,
  }));

  const changeBoxActivation = useCallback(
    async ({ deactivationReason }: BoxForListing): Promise<void> => {
      try {
        if (box.isActive && deactivationReason) {
          await deactivateBox({ boxId: box.boxId, deactivationReason }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Box has been successfully deactivated', { ns: 'common' }));
        } else {
          await activateBox(box.boxId).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Box has been successfully activated', { ns: 'common' }));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when changing box status', { ns: 'common' }));
      } finally {
        closeModal();
      }
    },
    [box.isActive, box.boxId, deactivateBox, t, activateBox, closeModal],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: box.isActive ? openModal : changeBoxActivation })}

      <Modal className="max-w-[400px]" isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={changeBoxActivation} className="flex flex-col">
          {/* <div className="mb-2">
            <WarningPing type="warning" />
          </div> */}
          <div className="font-semibold text-xl mb-3 font-tektur">{t('Box deactivation')}</div>

          <div className="text-primaryLight font-normal">
            {t('Are you sure you want to deactivate this box? You must select deactivation reason!')}
          </div>

          <div className="my-8">
            <Form.Item
              label={t('Deactivation reason')}
              name="deactivationReason"
              rules={[{ required: true, message: `${t('Please, select deactivation reason')}!` }]}
            >
              <Select options={deactivationReasonOptions} placeholder={t('Select deactivation reason')} bordered />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit">{t('Save', { ns: 'common' })}</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
